import Vue from 'vue'
import ApiService from '@/common/api.service'

export const state = {
  /*
   * eventProfilesSelectionSets is a "grouping" of multiple profiles that are
   * selected. For example, one might wish to have a "global" set of
   * selected profiles that is persistent across all screens or one
   * might want to create a set of selected profiles for a particular
   * individual/group of widgets.
   */
  eventProfilesSelectionSets: {}
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the sets of selected profiles.
   *
   * @param state Object
   *
   * @return Object
   */
  eventProfilesSelectionSets (state) {
    return state.eventProfilesSelectionSets
  }
}

export const actions = {
  /**
   * Query/find one or more profiles.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`events/profiles`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Query/find one or more profiles configured for monitored channels.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryByMonitoredChannel (context, params) {
    return ApiService
      .get(`events/profiles/monitored`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Query/find one or more profiles configured for client channels.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryByClientChannel (context, params) {
    return ApiService
      .get(`events/profiles/client`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Loads the selected profile by id and then set it as the selected profile.
   *
   * @param context   Object
   * @param set       String
   * @param profileId int
   *
   * @return Promise
   */
  loadAndSetSelectedProfile (context, { set, profileId }) {
    return ApiService
      .get(`events/profiles/profile-only/${profileId}`)
      .then((response) => {
        context.commit('setSelectedProfile', { set: set, profile: response.data.data })
        return response
      })
  }
}

export const mutations = {
  /**
   * Add a map or array of object profiles that have been selected within
   * the specified set.
   *
   * @param state    Object
   * @param set      String
   * @param profiles Array|Map
   */
  addSelectedProfiles (state, { set, profiles }) {
    if (!state.eventProfilesSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.eventProfilesSelectionSets, set, {})
    }

    profiles.forEach(profile => {
      Vue.set(state.eventProfilesSelectionSets[set], profile.id, profile)
    })
  },

  /**
   * Set a single selected profile, all other selected profiles
   * in the set will be cleared.
   *
   * @param state  Object
   * @param set    String
   * @param profile Object
   */
  setSelectedProfile (state, { set, profile }) {
    Vue.set(state.eventProfilesSelectionSets, set, {})
    Vue.set(state.eventProfilesSelectionSets[set], profile.id, profile)
  },

  /**
   * Removes an array of profiles from the set's list of selected profiles.
   * The array must consist of the id's of the profiles that are no
   * longer selected.
   *
   * @param state    Object
   * @param set      String
   * @param profiles Array
   */
  removeSelectedProfiles (state, { set, profiles }) {
    if (state.eventProfilesSelectionSets.hasOwnProperty(set)) {
      profiles.forEach(profile => {
        Vue.delete(state.eventProfilesSelectionSets[set], profile.id)
      })
    }
  },

  /**
   * Clears all selected profiles.
   *
   * @param state  Object
   */
  clearAllSelectedProfiles (state) {
    Vue.set(state, 'eventProfilesSelectionSets', {})
  },

  /**
   * Clears all selected profiles within a specified set.
   *
   * @param state  Object
   * @param set      String
   */
  clearSelectedProfiles (state, set) {
    if (state.eventProfilesSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.eventProfilesSelectionSets, set, {})
    }
  },

  /**
   * Clears all profiles.
   *
   * @param state Object
   */
  purge (state) {
    state.eventProfilesSelectionSets = {}
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/events/profiles/findEventProfilesWidgetModule.js